import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getAudienceDetail, refreshAudience, archiveAudience, exportAudienceCsv, exportAudienceMessaging } from '../config/api';
import { toast } from 'react-toastify';
import useIsMobile from '../hooks/useIsMobile';

const AudienceDetail = () => {
  const isMobile = useIsMobile();
  const { id } = useParams();
  const navigate = useNavigate();

  const [audience, setAudience] = useState(null);
  const [loading, setLoading] = useState(true);

  // Date filter for A/B metrics
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Refresh confirmation modal state
  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const [refreshDiff, setRefreshDiff] = useState(null);

  // Messaging export loading
  const [exportingChannel, setExportingChannel] = useState(null);

  // CSV export loading
  const [exportingCsv, setExportingCsv] = useState(false);

  // Staleness guard for concurrent fetches
  const fetchIdRef = useRef(0);

  useEffect(() => {
    fetchDetail();
  }, [id]);

  const fetchDetail = async (dateParams = {}) => {
    const currentFetchId = ++fetchIdRef.current;
    setLoading(true);
    try {
      const params = {};
      if (dateParams.start_date || startDate) params.start_date = dateParams.start_date || startDate;
      if (dateParams.end_date || endDate) params.end_date = dateParams.end_date || endDate;
      const res = await getAudienceDetail(id, params);
      if (currentFetchId !== fetchIdRef.current) return; // stale, discard
      if (res && res.data) {
        setAudience(res.data);
      } else {
        setAudience(null);
      }
    } catch (err) {
      if (currentFetchId !== fetchIdRef.current) return;
      setAudience(null);
    } finally {
      if (currentFetchId === fetchIdRef.current) setLoading(false);
    }
  };

  const handleDateFilterChange = (field, value) => {
    const newStart = field === 'start_date' ? value : startDate;
    const newEnd = field === 'end_date' ? value : endDate;
    if (field === 'start_date') setStartDate(value);
    if (field === 'end_date') setEndDate(value);
    // Re-fetch with new dates
    fetchDetail({ start_date: newStart, end_date: newEnd });
  };

  const handleCopyId = (formattedId) => {
    navigator.clipboard.writeText(formattedId);
    toast.success(`Copied ${formattedId} to clipboard!`);
  };

  const handleRefresh = async () => {
    try {
      toast.info('Re-evaluating snapshot filters...');
      const res = await refreshAudience(id);
      toast.success('Audience snapshot refreshed!');
      fetchDetail();
    } catch (err) {
      if (err.requires_confirmation) {
        // Show diff confirmation modal
        setRefreshDiff(err.responseData);
        setShowRefreshModal(true);
      } else {
        toast.error(err.message || 'Failed to refresh audience');
      }
    }
  };

  const handleConfirmRefresh = async () => {
    setShowRefreshModal(false);
    try {
      toast.info('Applying refresh...');
      await refreshAudience(id, { confirm: true });
      toast.success('Audience snapshot refreshed!');
      fetchDetail();
    } catch (err) {
      toast.error(err.message || 'Failed to refresh audience');
    } finally {
      setRefreshDiff(null);
    }
  };

  const handleArchive = async () => {
    if (!window.confirm('Are you sure you want to archive this audience?')) return;
    try {
      await archiveAudience(id);
      toast.success('Audience archived');
      navigate('/audiences');
    } catch (err) {
      if (err.status === 400) {
        // Show the backend message about blocking campaigns
        toast.error(err.message || 'Cannot archive: audience is currently in use.', { autoClose: 8000 });
      } else {
        toast.error(err.message || 'Failed to archive audience');
      }
    }
  };

  const handleExportMessaging = async (channel) => {
    setExportingChannel(channel);
    try {
      const result = await exportAudienceMessaging(audience.id, channel);
      if (!result) return; // auth redirect happened
      const exported = result.totalExported || '?';
      const removed = result.removedOptouts || '0';
      toast.success(`Exported ${exported} users (${removed} inactive/opted-out removed)`);
    } catch (err) {
      toast.error(err.message || `Failed to export for ${channel}`);
    } finally {
      setExportingChannel(null);
    }
  };

  const handleExportCsv = async () => {
    setExportingCsv(true);
    try {
      await exportAudienceCsv(audience.id);
    } catch (err) {
      toast.error(err.message || 'Failed to export CSV');
    } finally {
      setExportingCsv(false);
    }
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading audience detail...</div>;
  }

  if (!audience) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444' }}>Audience not found.</div>;
  }

  const formattedId = audience.formattedId || `AUD-${audience.id}`;

  const getFilterDisplay = (flt) => {
    const { type, params = {} } = flt;
    switch (type) {
      case 'order_count': {
        const min = params.min != null ? params.min : 0;
        const max = params.max != null && params.max !== '' ? params.max : null;
        if (min === 0 && max === 0) return { title: 'Order Count', text: 'Registered users with 0 orders' };
        if (max === null) return { title: 'Order Count', text: `Minimum ${min}+ orders` };
        return { title: 'Order Count', text: `Between ${min} and ${max} orders` };
      }
      case 'first_order_date': {
        const from = params.from || 'Beginning';
        const to = params.to || 'Today';
        return { title: 'First Order Date Range', text: `First order placed from ${from} to ${to}` };
      }
      case 'last_order_date': {
        if (params.older_than_days) {
          return { title: 'Last Order Date', text: `Last order was more than ${params.older_than_days} days ago` };
        }
        const from = params.from || 'Beginning';
        const to = params.to || 'Today';
        return { title: 'Last Order Date Range', text: `Last order placed from ${from} to ${to}` };
      }
      case 'signup_date': {
        const from = params.from || 'Beginning';
        const to = params.to || 'Today';
        return { title: 'Signup Date Range', text: `Account created from ${from} to ${to}` };
      }
      case 'last_active': {
        return { title: 'App Activity', text: `Active on app within last ${params.within_days || 30} days` };
      }
      case 'ordered_category': {
        const vals = Array.isArray(params.values) ? params.values.join(', ') : params.values || 'None';
        return { title: 'Ordered Category', text: `Bought items from: ${vals}` };
      }
      case 'ordered_skus': {
        const vals = Array.isArray(params.values) ? params.values.join(', ') : params.values || 'None';
        return { title: 'Ordered Product (SKU)', text: `Bought SKUs: ${vals}` };
      }
      case 'warehouse': {
        const ids = Array.isArray(params.ids) ? params.ids.join(', ') : params.ids || 'None';
        return { title: 'Hub / Warehouse', text: `Fulfillment Hub IDs: ${ids}` };
      }
      case 'specific_users': {
        const count = Array.isArray(params.values) ? params.values.length : 0;
        return { title: 'Specific Users List', text: `${count} specific user IDs / phone numbers uploaded` };
      }
      case 'audience': {
        const ids = Array.isArray(params.audience_ids) ? params.audience_ids.map(i => `AUD-${i}`).join(', ') : '';
        return { title: 'Referenced Audience', text: `Targeting existing audience: ${ids}` };
      }
      default:
        return { title: flt.type || 'Filter Condition', text: flt.label || JSON.stringify(params) };
    }
  };

  return (
    <div className="container-fluid" style={{ padding: '24px', backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      
      {/* Top Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <button
            onClick={() => navigate('/audiences')}
            style={{ border: 'none', background: 'none', color: '#2563eb', fontWeight: '600', cursor: 'pointer', fontSize: '13px', padding: 0, marginBottom: '4px' }}
          >
            ← Back to Audiences
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#0f172a', margin: 0 }}>
              {audience.name}
            </h2>            <span
              onClick={() => handleCopyId(formattedId)}
              title="Click to copy ID"
              style={{
                background: '#f1f5f9',
                border: '1px solid #cbd5e1',
                color: '#1e293b',
                padding: '4px 10px',
                borderRadius: '6px',
                fontFamily: 'monospace',
                fontSize: '13px',
                fontWeight: '700',
                cursor: 'pointer',
              }}
            >
              {formattedId}
            </span>

            <span
              style={{
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: '600',
                background: audience.type === 'static' ? '#e0f2fe' : '#fef3c7',
                color: audience.type === 'static' ? '#0369a1' : '#b45309',
              }}
            >
              {audience.type === 'static' ? 'Static Snapshot' : 'Dynamic Live'}
            </span>
          </div>
        </div>

        {/* Action Controls */}
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {audience.type === 'static' && (
            <button
              onClick={handleRefresh}
              className="btn btn-outline-secondary"
              style={{ borderRadius: '8px', padding: '8px 16px', fontWeight: '600', fontSize: '13px' }}
            >
              Refresh Snapshot
            </button>
          )}

          <button
            onClick={handleExportCsv}
            disabled={exportingCsv}
            className="btn btn-success"
            style={{ borderRadius: '8px', padding: '8px 16px', fontWeight: '600', fontSize: '13px', background: '#16a34a', border: 'none' }}
          >
            {exportingCsv ? '⏳ Exporting...' : 'Export CSV'}
          </button>

          <button
            onClick={() => handleExportMessaging('whatsapp')}
            disabled={exportingChannel === 'whatsapp'}
            className="btn btn-outline-success"
            style={{ borderRadius: '8px', padding: '8px 16px', fontWeight: '600', fontSize: '13px' }}
          >
            {exportingChannel === 'whatsapp' ? '⏳ Exporting...' : '📱 Export for WhatsApp'}
          </button>

          <button
            onClick={() => handleExportMessaging('sms')}
            disabled={exportingChannel === 'sms'}
            className="btn btn-outline-info"
            style={{ borderRadius: '8px', padding: '8px 16px', fontWeight: '600', fontSize: '13px' }}
          >
            {exportingChannel === 'sms' ? '⏳ Exporting...' : '💬 Export for SMS'}
          </button>

          <button
            onClick={handleArchive}
            className="btn btn-outline-danger"
            style={{ borderRadius: '8px', padding: '8px 16px', fontWeight: '600', fontSize: '13px' }}
          >
            Archive
          </button>
        </div>
      </div>

      <div className="row">
        
        {/* Left Column — Summary & Filters */}
        <div className="col-lg-8">
          
          {/* Plain English Summary Box */}
          <div style={{ background: '#ffffff', borderRadius: '12px', padding: '20px', marginBottom: '20px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <h4 style={{ fontSize: '13px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
              Auto-Generated Summary
            </h4>

            <div style={{ fontSize: '16px', fontWeight: '600', color: '#0f172a', lineHeight: '1.5', background: '#f8fafc', padding: '14px', borderRadius: '8px', borderLeft: '4px solid #2563eb' }}>
              "{audience.description_auto} ({audience.member_count?.toLocaleString()} total users)"
            </div>
          </div>

          {/* Filter Rules List */}
          <div style={{ background: '#ffffff', borderRadius: '12px', padding: '20px', marginBottom: '20px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', marginBottom: '16px' }}>
              Filter Conditions Breakdown
            </h4>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {audience.filters?.map((flt, idx) => {
                const info = getFilterDisplay(flt);
                const isOr = idx > 0 && flt.combinator === 'or';

                return (
                  <React.Fragment key={idx}>
                    {idx > 0 && (
                      <div style={{ textTransform: 'uppercase', fontSize: '11px', fontWeight: '800', color: isOr ? '#d97706' : '#2563eb', textAlign: 'center', margin: '-4px 0' }}>
                        <span style={{ background: isOr ? '#fffbeb' : '#eff6ff', padding: '2px 10px', borderRadius: '10px', border: isOr ? '1px solid #fde68a' : '1px solid #bfdbfe' }}>
                          {flt.combinator ? flt.combinator.toUpperCase() : 'AND'}
                        </span>
                      </div>
                    )}

                    <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', padding: '14px 18px', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '14px', boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
                      <span style={{ fontSize: '11px', fontWeight: '800', background: flt.mode === 'exclude' ? '#fee2e2' : '#dcfce7', color: flt.mode === 'exclude' ? '#b91c1c' : '#15803d', padding: '4px 10px', borderRadius: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {flt.mode || 'INCLUDE'}
                      </span>
                      
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a' }}>
                          {info.title}
                        </div>
                        <div style={{ fontSize: '13px', color: '#475569', marginTop: '2px', fontWeight: '500' }}>
                          {info.text}
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* A/B Split Child Groups & Performance Analytics */}
          {audience.child_groups && audience.child_groups.length > 0 && (
            <div style={{ background: '#ffffff', borderRadius: '12px', padding: '20px', marginBottom: '20px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
                <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>A/B Split Test Conversion Performance</span>
                  <span style={{ fontSize: '11px', background: '#dcfce7', color: '#15803d', padding: '2px 8px', borderRadius: '10px' }}>Seeded Hash Reproducible</span>
                </h4>

                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {audience.ab_lift != null && (
                    <span style={{ fontSize: '12px', fontWeight: '700', background: audience.ab_lift > 0 ? '#dcfce7' : '#f1f5f9', color: audience.ab_lift > 0 ? '#15803d' : '#475569', padding: '4px 10px', borderRadius: '12px' }}>
                      {audience.ab_lift > 0 ? `+${audience.ab_lift}% Conversion Lift` : `${audience.ab_lift}% Lift (Baseline)`}
                    </span>
                  )}
                  {audience.ab_revenue_lift != null && (
                    <span style={{ fontSize: '12px', fontWeight: '700', background: '#f0fdf4', color: '#166534', padding: '4px 10px', borderRadius: '12px', border: '1px solid #bbf7d0' }}>
                      +₹{audience.ab_revenue_lift} incremental rev/member
                    </span>
                  )}
                </div>
              </div>

              {/* Date Range Filter for A/B Metrics */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#475569' }}>Measure period:</span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => handleDateFilterChange('start_date', e.target.value)}
                  style={{ padding: '5px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                />
                <span style={{ fontSize: '12px', color: '#64748b' }}>to</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => handleDateFilterChange('end_date', e.target.value)}
                  style={{ padding: '5px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                />
                {(startDate || endDate) && (
                  <button
                    onClick={() => { setStartDate(''); setEndDate(''); fetchDetail({ start_date: '', end_date: '' }); }}
                    style={{ border: 'none', background: '#fee2e2', color: '#dc2626', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}
                  >
                    Clear
                  </button>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
                {audience.child_groups.map((grp) => (
                  <div
                    key={grp.id}
                    style={{
                      background: grp.type === 'treatment' ? '#eff6ff' : '#f8fafc',
                      border: grp.type === 'treatment' ? '1px solid #bfdbfe' : '1px solid #cbd5e1',
                      borderRadius: '10px',
                      padding: '16px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span
                        onClick={() => handleCopyId(grp.formattedId)}
                        style={{ fontFamily: 'monospace', fontWeight: '700', fontSize: '13px', background: '#ffffff', padding: '2px 6px', borderRadius: '4px', border: '1px solid #cbd5e1', cursor: 'pointer' }}
                      >
                        {grp.formattedId}
                      </span>
                      <span style={{ fontSize: '12px', fontWeight: '700', color: grp.type === 'treatment' ? '#1d4ed8' : '#475569' }}>
                        {grp.pct}% Split
                      </span>
                    </div>

                    <div style={{ fontWeight: '700', color: '#0f172a', fontSize: '14px', marginBottom: '12px' }}>
                      {grp.name}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr 1fr 1fr', gap: '8px', background: '#ffffff', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                      <div>
                        <div style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', fontWeight: '700' }}>Members</div>
                        <div style={{ fontSize: '15px', fontWeight: '800', color: '#0f172a', marginTop: '2px' }}>
                          {grp.member_count?.toLocaleString() || 0}
                        </div>
                      </div>

                      <div>
                        <div style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', fontWeight: '700' }}>Orders</div>
                        <div style={{ fontSize: '15px', fontWeight: '800', color: '#2563eb', marginTop: '2px' }}>
                          {grp.orders_count?.toLocaleString() || 0}
                        </div>
                      </div>

                      <div>
                        <div style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', fontWeight: '700' }}>Conv %</div>
                        <div style={{ fontSize: '15px', fontWeight: '800', color: grp.type === 'treatment' ? '#16a34a' : '#475569', marginTop: '2px' }}>
                          {grp.conversion_rate || 0}%
                        </div>
                      </div>

                      <div>
                        <div style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', fontWeight: '700' }}>Revenue</div>
                        <div style={{ fontSize: '15px', fontWeight: '800', color: '#7c3aed', marginTop: '2px' }}>
                          ₹{(grp.total_revenue || 0).toLocaleString()}
                        </div>
                      </div>

                      <div>
                        <div style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', fontWeight: '700' }}>Rev/Member</div>
                        <div style={{ fontSize: '15px', fontWeight: '800', color: '#0891b2', marginTop: '2px' }}>
                          ₹{(grp.revenue_per_member || 0).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Usage Log Table ("Where has this audience been used?") */}
          <div style={{ background: '#ffffff', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', marginBottom: '16px' }}>
              Audience Usage Audit Log ("Where is this used?")
            </h4>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', color: '#334155' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textTransform: 'uppercase', fontSize: '11px', color: '#64748b' }}>
                    <th style={{ padding: '10px 14px', textAlign: 'left' }}>System / Channel</th>
                    <th style={{ padding: '10px 14px', textAlign: 'left' }}>Campaign / Reference</th>
                    <th style={{ padding: '10px 14px', textAlign: 'left' }}>Used Date</th>
                    <th style={{ padding: '10px 14px', textAlign: 'right' }}>Created By</th>
                  </tr>
                </thead>
                <tbody>
                  {audience.usage_logs?.length === 0 ? (
                    <tr>
                      <td colSpan="4" style={{ textAlign: 'center', padding: '20px', color: '#94a3b8' }}>
                        This audience has not been used in any downstream campaign yet.
                      </td>
                    </tr>
                  ) : (
                    audience.usage_logs?.map((log) => (
                      <tr key={log.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '10px 14px', fontWeight: '600', color: '#2563eb' }}>
                          {log.system}
                        </td>
                        <td style={{ padding: '10px 14px', color: '#0f172a', fontWeight: '500' }}>
                          {log.reference}
                        </td>
                        <td style={{ padding: '10px 14px', color: '#64748b' }}>
                          {new Date(log.used_at).toLocaleDateString()} {new Date(log.used_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td style={{ padding: '10px 14px', textAlign: 'right', color: '#64748b' }}>
                          {log.actor}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Right Column — Info Sidebar */}
        <div className="col-lg-4">
          <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', marginBottom: '20px' }}>
            <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', marginBottom: '16px' }}>
              Audience Stats
            </h4>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Total Member Count</div>
                <div style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a' }}>
                  {audience.member_count?.toLocaleString()}
                </div>
              </div>

              <div>
                <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Created By</div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#334155' }}>
                  {audience.created_by || '—'}
                </div>
              </div>

              <div>
                <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Created Date</div>
                <div style={{ fontSize: '13px', color: '#334155' }}>
                  {(() => {
                    const rawDate = audience.created_at || audience.createdAt;
                    if (!rawDate) return '—';
                    const d = new Date(rawDate);
                    return !isNaN(d.getTime()) ? `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : '—';
                  })()}
                </div>
              </div>

              {audience.refreshed_at && (
                <div>
                  <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Last Refreshed</div>
                  <div style={{ fontSize: '13px', color: '#334155' }}>
                    {(() => {
                      const d = new Date(audience.refreshed_at);
                      return !isNaN(d.getTime()) ? `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : '—';
                    })()}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Quick Integration Card */}
          <div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', borderRadius: '12px', padding: '20px', color: '#ffffff' }}>
            <h5 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>Integration Token</span>
            </h5>
            <p style={{ fontSize: '12px', color: '#94a3b8', lineHeight: '1.4', marginBottom: '12px' }}>
              Pass this ID directly to Push Notifications, WhatsApp, Coupons, or Promocash.
            </p>
            <div style={{ background: '#334155', padding: '8px 12px', borderRadius: '6px', fontFamily: 'monospace', fontSize: '13px', color: '#38bdf8', fontWeight: '700', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>"audience_id": "{formattedId}"</span>
              <button
                onClick={() => handleCopyId(formattedId)}
                style={{ border: 'none', background: 'none', color: '#ffffff', cursor: 'pointer' }}
              >
                📋
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Refresh Diff Confirmation Modal */}
      {showRefreshModal && refreshDiff && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: '#ffffff', borderRadius: '12px', padding: '28px', width: '500px', maxWidth: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{ background: '#fef3c7', borderRadius: '50%', width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>
                🔄
              </div>
              <h4 style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a', margin: 0 }}>
                Confirm Refresh
              </h4>
            </div>
            <p style={{ fontSize: '14px', color: '#475569', marginBottom: '16px', lineHeight: '1.5' }}>
              Refreshing this audience will significantly change its membership:
            </p>
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '12px', textAlign: 'center' }}>
                <div>
                  <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>Previous</div>
                  <div style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a' }}>
                    {(refreshDiff.previous_count || 0).toLocaleString()}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>New Count</div>
                  <div style={{ fontSize: '20px', fontWeight: '800', color: '#2563eb' }}>
                    {(refreshDiff.new_count || 0).toLocaleString()}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>Change</div>
                  <div style={{ fontSize: '20px', fontWeight: '800', color: (refreshDiff.diff || 0) >= 0 ? '#16a34a' : '#dc2626' }}>
                    {(refreshDiff.diff || 0) >= 0 ? '+' : ''}{(refreshDiff.diff || 0).toLocaleString()}
                  </div>
                </div>
              </div>
              {refreshDiff.change_pct != null && (
                <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '13px', color: '#64748b' }}>
                  ({refreshDiff.change_pct}% change in membership)
                </div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => { setShowRefreshModal(false); setRefreshDiff(null); }}
                className="btn btn-outline-secondary"
                style={{ borderRadius: '8px', padding: '8px 16px', fontWeight: '600', fontSize: '13px' }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRefresh}
                className="btn btn-primary"
                style={{ borderRadius: '8px', padding: '8px 16px', fontWeight: '600', fontSize: '13px' }}
              >
                Yes, Apply Refresh
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AudienceDetail;
